import React from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import { GetLoadAllocation_getLoadAllocation } from '../../../types/schemaTypes';
import './DailySandAllocations.scss';

interface DailySandAllocationsDetailsTableProps {
    dateList: string[];
    loadAllocationDetails: Record<string, GetLoadAllocation_getLoadAllocation[]>;
    loading: boolean;
    isEditMode?: boolean;
    editedTons?: Record<string, number | null>;
    jobSummaryIdMap?: Record<string, number>;
    onCellChange?: (jobSummaryId: number, dayIndex: number, value: number | null) => void;
}

const DAY_COUNT = 14;

const getEditKey = (jobSummaryId: number, dayIndex: number) => `${jobSummaryId}_${dayIndex}`;
const getJobSummaryMapKey = (
    lofaId: number,
    proppantId: number,
    sandPO: string,
    transportationPO: string,
) => `${lofaId}_${proppantId}_${sandPO}_${transportationPO}`;

const DailySandAllocationsDetailsTable = ({
    dateList,
    loadAllocationDetails,
    loading,
    isEditMode = false,
    editedTons = {},
    jobSummaryIdMap = {},
    onCellChange,
}: DailySandAllocationsDetailsTableProps): React.ReactElement => {
    const finalTotalList = new Array(DAY_COUNT).fill(0);

    const getEffectiveWeight = (
        jobSummaryId: number | undefined,
        dayIndex: number,
        rawValue: number | null | undefined,
    ): number | null => {
        if (
            jobSummaryId !== undefined &&
            Object.prototype.hasOwnProperty.call(editedTons, getEditKey(jobSummaryId, dayIndex))
        ) {
            const edited = editedTons[getEditKey(jobSummaryId, dayIndex)];
            return edited !== null && Number.isFinite(edited) ? edited : null;
        }
        return rawValue ?? null;
    };

    return (
        <Col>
            <Row>
                <Table
                    bordered
                    className={
                        loading
                            ? 'DailySandAllocationsTable DailySandAllocationsTable-Loading'
                            : 'DailySandAllocationsTable'
                    }
                >
                    <tr className="DailySandAllocationsTable-Dates">
                        <th></th>
                        <th></th>
                        <th></th>
                        {dateList.map((date, index) => (
                            <th key={index} className={date.includes('|') ? 'DailySandAllocations-Weekend' : ''}>
                                {date.replace('|', '')}
                            </th>
                        ))}
                    </tr>

                    {Object.keys(loadAllocationDetails).map((proppant) => {
                        const totalList = new Array(DAY_COUNT).fill(0);
                        return (
                            <React.Fragment key={proppant}>
                                <tr>
                                    <th className="DailySandAllocations-Header DailySandAllocations-ClmLg">
                                        {proppant}
                                    </th>
                                    <th className="DailySandAllocations-Header DailySandAllocations-ClmLg">Sand PO</th>
                                    <th className="DailySandAllocations-Header DailySandAllocations-ClmLg">Transportation PO</th>
                                </tr>

                                {loadAllocationDetails[proppant].map((loadAllocationDetail, index) => {
                                    const jobSummaryId =
                                        jobSummaryIdMap[
                                            getJobSummaryMapKey(
                                                loadAllocationDetail.lofaId,
                                                loadAllocationDetail.proppantId,
                                                loadAllocationDetail.sandPO,
                                                loadAllocationDetail.transportationPO,
                                            )
                                        ];
                                    const row = loadAllocationDetail as unknown as Record<string, number | null | undefined>;
                                    const rowEditable = isEditMode && jobSummaryId !== undefined;

                                    const cellValues: (number | null)[] = [];
                                    for (let i = 0; i < DAY_COUNT; i++) {
                                        const v = getEffectiveWeight(jobSummaryId, i, row[`weightonDate${i + 1}`]);
                                        cellValues.push(v);
                                        totalList[i] = totalList[i] + (v ?? 0);
                                    }

                                    return (
                                        <tr key={index}>
                                            <td className="DailySandAllocations-ClmLg">
                                                {loadAllocationDetail.lofaName}
                                            </td>
                                            <td className="DailySandAllocations-ClmLg">
                                                {loadAllocationDetail.sandPO}
                                            </td>
                                            <td className="DailySandAllocations-ClmLg">
                                                {loadAllocationDetail.transportationPO}
                                            </td>
                                            {cellValues.map((value, i) => (
                                                <td key={i}>
                                                    {rowEditable ? (
                                                        <input
                                                            inputMode="decimal"
                                                            type="text"
                                                            className="DailySandAllocations-Edit-Input"
                                                            value={value === null ? '' : String(value)}
                                                            maxLength={9}
                                                            onChange={(e) => {
                                                                if (!onCellChange || jobSummaryId === undefined) {
                                                                    return;
                                                                }
                                                                const raw = e.target.value;
                                                                if (raw === '') {
                                                                    onCellChange(jobSummaryId, i, null);
                                                                    return;
                                                                }
                                                                if (!/^\d{0,6}(?:\.\d{0,2})?$/.test(raw)) {
                                                                    return;
                                                                }
                                                                const parsed = parseFloat(raw);
                                                                onCellChange(
                                                                    jobSummaryId,
                                                                    i,
                                                                    Number.isFinite(parsed) ? parsed : null,
                                                                );
                                                            }}
                                                        />
                                                    ) : (
                                                        value?.toFixed(2) ?? '-'
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}

                                <tr className="DailySandAllocationsTable-Total-Row">
                                    <th
                                        colSpan={2}
                                        className="DailySandAllocationsTable-Total-Header"
                                    >{`Total ${proppant} Supply`}</th>
                                    <th></th>
                                    {totalList.map((total, index) => {
                                        finalTotalList[index] = finalTotalList[index] + total;
                                        return <th key={index}>{total?.toFixed(2)}</th>;
                                    })}
                                </tr>
                                <tr><td colSpan={16}>&nbsp;</td></tr>
                            </React.Fragment>
                        );
                    })}

                    <tr className="DailySandAllocationsTable-Final-Total-Row">
                        <th colSpan={2} className="DailySandAllocationsTable-Total-Header">
                            Total
                        </th>
                        <th></th>
                        {finalTotalList.map((finalTotal, index) => (
                            <th key={index}>{finalTotal?.toFixed(2)}</th>
                        ))}
                    </tr>
                </Table>
            </Row>
        </Col>
    );
};

export default DailySandAllocationsDetailsTable;
